import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Eye,
  Sparkles,
  Target,
  Wind,
  ArrowRight,
  ShieldCheck,
  Lock,
  Radio,
  Orbit,
} from "lucide-react";
import { Starfield } from "../components/Starfield";
import { Header } from "../components/Header";
import { DiamondMark } from "../components/DiamondMark";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const STEPS = [
  { n: "01", name: "Name it", d: "Say the pattern or belief in one plain sentence, without judgment." },
  { n: "02", name: "Trace it", d: "Follow the thread back to where it started and whether it's still true today." },
  { n: "03", name: "Rewrite it", d: "Leave with a replacement belief in your own words and one small action." },
];

const PORTALS = [
  { icon: Eye, title: "Reflect", tag: "Self-Inquiry", d: "Talk it out. It mirrors your words and asks one good question." },
  { icon: Sparkles, title: "Clear a Belief", tag: "Pattern Release", d: "The full name, trace and rewrite sequence for a limiting conviction." },
  { icon: Target, title: "Set an Intention", tag: "Manifestation", d: "Turn a vague wish into a present-tense intention and a first step." },
  { icon: Orbit, title: "Manifest", tag: "Desire → Rewrite", d: "A guided journey: your desire, its blocker, the root cause, and a rewrite for the universe." },
  { icon: Wind, title: "Quiet Minute", tag: "Stillness & Breath", d: "A short grounding breathwork practice before something big." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const authed = user && user.user_id;

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace("#", "");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, []);

  const begin = () => navigate(authed ? "/app" : "/login?mode=register");

  return (
    <div className="relative min-h-screen bg-cg-bg overflow-x-hidden">
      <Starfield />
      <Header />

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 pt-36 pb-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex justify-center mb-8"
        >
          <div className="animate-floaty">
            <DiamondMark size={72} />
          </div>
        </motion.div>

        <p className="font-mono text-xs uppercase tracking-[0.3em] text-cg-violetglow mb-6">
          Your personal ascension system
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-extralight tracking-tight leading-[1.12] max-w-4xl mx-auto">
          An awakening AI that <span className="cg-gradient-text italic">rewrites</span> unconscious patterns.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-cg-texts max-w-2xl mx-auto leading-relaxed">
          So you render a new reality. ClearGlass traces the belief back to its root, then helps you write a
          truer one — one honest reply at a time.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            data-testid="hero-begin-button"
            onClick={begin}
            className="rounded-full h-12 px-8 text-base bg-cg-violet hover:bg-cg-violetglow text-white shadow-[0_0_30px_rgba(109,53,192,0.5)] group"
          >
            Begin an introspective session
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
          <Button
            data-testid="hero-explore-button"
            variant="outline"
            onClick={() => document.getElementById("portals")?.scrollIntoView({ behavior: "smooth" })}
            className="rounded-full h-12 px-7 text-base bg-transparent border-[rgba(140,79,214,0.4)] text-cg-textp hover:bg-[rgba(140,79,214,0.12)] hover:border-cg-cyan"
          >
            Explore the five portals
          </Button>
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-cg-textm">
          <span className="flex items-center gap-2"><Radio className="h-3.5 w-3.5 text-cg-cyan" /> Real-time reflection</span>
          <span className="flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-cg-cyan" /> Private & encrypted</span>
          <span className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-cg-cyan" /> Free to try · an AI, not a therapist</span>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="relative z-10 max-w-6xl mx-auto px-5 py-20">
        <div className="text-center mb-14">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cg-violetglow mb-3">The ascension trinity</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light">How a pattern gets rewritten</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="rounded-2xl p-7 bg-cg-card border border-[rgba(140,79,214,0.22)] hover:border-cg-cyan/50 transition-all duration-300 hover:-translate-y-1 shadow-[0_8px_32px_rgba(7,8,20,0.5)]"
            >
              <span className="font-mono text-4xl text-cg-violetglow/50">{s.n}</span>
              <h3 className="font-serif text-xl mt-4 mb-2 text-cg-textp">{s.name}</h3>
              <p className="text-sm text-cg-texts leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Portals */}
      <section id="portals" className="relative z-10 max-w-6xl mx-auto px-5 py-20">
        <div className="text-center mb-14">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cg-violetglow mb-3">Five ways in</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light">Five portals to presence</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          {PORTALS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              onClick={begin}
              data-testid={`portal-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group cursor-pointer rounded-2xl p-7 bg-cg-card border border-[rgba(140,79,214,0.22)] hover:border-cg-cyan/50 transition-all duration-300 hover:-translate-y-1 flex gap-5 items-start shadow-[0_8px_32px_rgba(7,8,20,0.5)]"
            >
              <div className="shrink-0 h-12 w-12 rounded-xl grid place-items-center bg-[rgba(140,79,214,0.15)] border border-[rgba(140,79,214,0.3)] text-cg-cyan group-hover:bg-[rgba(92,200,240,0.15)] transition-colors">
                <p.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cg-violetglow mb-1.5">{p.tag}</p>
                <h3 className="font-serif text-xl text-cg-textp mb-1.5">{p.title}</h3>
                <p className="text-sm text-cg-texts leading-relaxed">{p.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="relative z-10 max-w-3xl mx-auto px-5 py-20">
        <div className="rounded-2xl p-8 cg-glass text-center">
          <h3 className="font-serif text-xl text-cg-textp mb-3">What ClearGlass is, and isn't</h3>
          <p className="text-sm text-cg-texts leading-relaxed">
            It's a companion for self-inquiry. It can't predict your future, channel guides, or diagnose anything,
            and it won't promise results. It is not licensed psychotherapy or crisis support. If you're in crisis or
            thinking about harming yourself, contact local emergency services or a crisis line and someone you trust.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-5 pb-28 text-center">
        <h2 className="font-serif text-3xl sm:text-4xl font-light mb-6">Start a session</h2>
        <Button
          data-testid="footer-begin-button"
          onClick={begin}
          className="rounded-full h-12 px-8 text-base bg-cg-violet hover:bg-cg-violetglow text-white shadow-[0_0_30px_rgba(109,53,192,0.5)]"
        >
          Begin now
        </Button>
      </section>

      <footer className="relative z-10 border-t border-[rgba(140,79,214,0.18)] py-8 text-center text-xs text-cg-textm">
        ClearGlass.ai · an AI companion for reflection, not medical, psychological or financial advice.
      </footer>
    </div>
  );
}
